import React, { useRef, useState, useEffect, Suspense } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box, CssBaseline, AppBar, Toolbar, Typography, Drawer,
    List, ListItemButton, ListItemIcon, ListItemText, Collapse,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Tooltip, Popover, Divider, CircularProgress,
} from '@mui/material';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import ScaleIcon from '@mui/icons-material/Scale';
import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CompassCalibrationIcon from '@mui/icons-material/CompassCalibration';
import MapIcon from '@mui/icons-material/Map';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';

import AppButton from './ui/AppButton';
import AppFooter from './AppFooter';
import LiveWeightStrip from './weighing/LiveWeightStrip';
import { getMe, logout } from '../services/auth/authService';
import { useTenantSettingsLive } from '../hooks/useTenantSettingsLive';
import { useGlobalEntityCacheInvalidation } from '../hooks/useGlobalEntityCacheInvalidation';
import { useEntityLabels } from '../hooks/useEntityLabels';
import { isNavItemHidden } from '../hooks/usePermission';
import { useColorMode } from '../theme/ColorModeProvider';
import { useLiveWeightDisplayPreference } from '../hooks/useLiveWeightDisplayPreference';

const headerLogoUrl = `${process.env.PUBLIC_URL}/WhiteLogo.png`;

const drawerWidthOpen = 240;
const drawerWidthClosed = 64;

// Flyout menus (permKey matches Nav.item.<key> in Table Permissions)
const usersMenu = [
    { label: 'User Accounts', icon: <PersonIcon fontSize="small" />, path: '/users', permKey: 'users.accounts' },
    { label: 'User Roles', icon: <GroupIcon fontSize="small" />, path: '/users/roles', permKey: 'users.roles' },
];

const loadsMenu = [
    { label: 'Weighing', path: '/loads/weighing', permKey: 'loads.weighing' },
    { label: 'Completed', path: '/loads/completed', permKey: 'loads.completed' },
    { label: 'Deleted', path: '/loads/deleted', permKey: 'loads.deleted' },
];

const permsMenu = [
    { label: 'Form Permissions', icon: <LockIcon fontSize="small" />, path: '/permissions/form', permKey: 'permissions.form' },
    { label: 'Table Permissions', icon: <SupervisedUserCircleIcon fontSize="small" />, path: '/permissions/table', permKey: 'permissions.table' },
];

const filterNavMenuItems = (parentKey, items) => {
    if (isNavItemHidden(parentKey)) return [];
    return items.filter((it) => !isNavItemHidden(it.permKey));
};

export default function AppLayout() {


    const navigate = useNavigate();
    const location = useLocation();

    useTenantSettingsLive();
    useGlobalEntityCacheInvalidation();
    const { commodity, account, location: locationLabels, laArea: laAreaLabels } = useEntityLabels();
    const { isDark, toggleColorMode } = useColorMode();
    const {
        enabled: liveWeightDisplayEnabled,
        toggleLiveWeightDisplay,
    } = useLiveWeightDisplayPreference();

    const { data: me } = useQuery({
        queryKey: ['me'],
        queryFn: ({ signal }) => getMe(signal),
        staleTime: 60_000,
    });
    const greetingName = me?.username || 'User';

    // collapsible sections (when drawer is open)
    const [loadsOpen, setLoadsOpen] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);
    const [permissionsOpen, setPermissionsOpen] = useState(false);

    // mini-variant drawer state
    const [navOpen, setNavOpen] = useState(false);

    // logout dialog
    const [confirmOpen, setConfirmOpen] = useState(false);

    // flyout state for collapsed drawer
    const [hoverMenu, setHoverMenu] = useState(null); // { id: 'users'|'loads'|'perms', anchorEl: HTMLElement }
    const flyoutPaperRef = useRef(null);

    const handleNav = (path) => navigate(path);
    const handleLogoutClick = () => setConfirmOpen(true);
    const handleConfirmLogout = async () => {
        try {
            await logout();
        } catch {
            // Even when API logout fails, keep local logout behavior.
        }
        setConfirmOpen(false);
        navigate('/login');
    };
    const handleCancelLogout = () => setConfirmOpen(false);

    const openFlyout = (id, anchorEl) => setHoverMenu({ id, anchorEl });

    const pointInRect = (x, y, r) =>
        !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

    // Close flyout only when pointer is outside BOTH the parent row and the flyout
    useEffect(() => {
        if (!hoverMenu || navOpen) return; // flyouts only when collapsed
        const onMove = (e) => {
            const anchorRect = hoverMenu.anchorEl?.getBoundingClientRect?.();
            const popRect = flyoutPaperRef.current?.getBoundingClientRect?.();
            const insideParent = pointInRect(e.clientX, e.clientY, anchorRect);
            const insidePopover = pointInRect(e.clientX, e.clientY, popRect);
            if (!insideParent && !insidePopover) {
                setHoverMenu(null);
            }
        };
        document.addEventListener('mousemove', onMove);
        return () => document.removeEventListener('mousemove', onMove);
    }, [hoverMenu, navOpen]);

    // Common styles so items align in both states
    const itemButtonSx = {
        minHeight: 44,
        justifyContent: navOpen ? 'initial' : 'center',
        px: 2,
    };
    const itemIconSx = {
        minWidth: 0,
        mr: navOpen ? 2 : 'auto',
        justifyContent: 'center',
    };
    const itemTextSx = { opacity: navOpen ? 1 : 0, transition: 'opacity 200ms' };

    const visibleUsersMenu = filterNavMenuItems('users', usersMenu);
    const visibleLoadsMenu = filterNavMenuItems('loads', loadsMenu);
    const visiblePermsMenu = filterNavMenuItems('permissions', permsMenu);

    const isNavActive = (basePath) => {
        if (basePath === '/') {
            return location.pathname === '/';
        }
        return (
            location.pathname === basePath ||
            location.pathname.startsWith(`${basePath}/`)
        );
    };

    // Flyout component: overlaps drawer edge to remove “dead zone”
    const Flyout = ({ id, items }) => {
        const open = hoverMenu?.id === id && !!hoverMenu.anchorEl;
        return (
            <Popover
                open={open}
                anchorEl={hoverMenu?.anchorEl}
                onClose={() => setHoverMenu(null)}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                disableAutoFocus
                disableEnforceFocus
                PaperProps={{
                    ref: flyoutPaperRef,
                    sx: {
                        pointerEvents: 'auto',
                        minWidth: 220,
                        py: 0.5,
                        ml: -1, // ~8px overlap to avoid a hover gap
                    },
                }}
            >
                <List dense disablePadding>
                    {items.map((it) => (
                        <ListItemButton
                            key={it.label}
                            onClick={() => { setHoverMenu(null); handleNav(it.path); }}
                            sx={{ px: 2 }}
                        >
                            {it.icon && <ListItemIcon sx={{ minWidth: 28 }}>{it.icon}</ListItemIcon>}
                            <ListItemText primary={it.label} />
                        </ListItemButton>
                    ))}
                </List>
            </Popover>
        );
    };

    return (
        <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline enableColorScheme />

            {/* AppBar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    transition: (theme) => theme.transitions.create(['width', 'margin']),
                }}
            >
                <Toolbar sx={{ minHeight: 64, alignItems: 'center', gap: 2 }}>
                    {/* Toggle nav open/closed */}
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => { setNavOpen((o) => !o); setHoverMenu(null); }}
                        sx={{ mr: 1 }}
                        aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
                    >
                        {navOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 48,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={headerLogoUrl}
                            alt="GenWeb"
                            onClick={() => navigate('/')}
                            sx={{
                                height: '100%',
                                maxWidth: 180,
                                cursor: 'pointer',
                                display: 'block',
                                objectFit: 'contain',
                                objectPosition: 'center center',
                                transform: 'translateY(-3px)',
                            }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title={liveWeightDisplayEnabled ? 'Hide live weight display' : 'Show live weight display'}>
                        <IconButton
                            color="inherit"
                            onClick={toggleLiveWeightDisplay}
                            aria-label={liveWeightDisplayEnabled ? 'Hide live weight display' : 'Show live weight display'}
                            aria-pressed={liveWeightDisplayEnabled}
                            sx={{
                                mr: 0.5,
                                bgcolor: liveWeightDisplayEnabled ? 'rgba(255,255,255,0.18)' : 'transparent',
                            }}
                        >
                            <ScaleOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                        <IconButton
                            color="inherit"
                            onClick={toggleColorMode}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            sx={{ mr: 0.5 }}
                        >
                            {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
                        </IconButton>
                    </Tooltip>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                        {`Hello, ${greetingName}`}
                    </Typography>
                    <AppButton onClick={handleLogoutClick}>
                        Logout
                    </AppButton>
                </Toolbar>
            </AppBar>

            {/* Collapsible (mini-variant) Drawer */}
            <Drawer
                variant="permanent"
                sx={(theme) => ({
                    width: navOpen ? drawerWidthOpen : drawerWidthClosed,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiDrawer-paper': {
                        width: navOpen ? drawerWidthOpen : drawerWidthClosed,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.standard,
                        }),
                    },
                })}
            >
                <Toolbar />

                <List disablePadding>

                    <Tooltip title={!navOpen ? 'Dashboard' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/') ? 'primary.main' : 'inherit' }}
                            >
                                <DashboardIcon />
                            </ListItemIcon>
                            <ListItemText primary="Dashboard" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>

                    {/* Simple routes (can keep Tooltip if you want labels when collapsed) */}
                    {!isNavItemHidden('accounts') && (
                    <Tooltip title={!navOpen ? account.plural : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/accounts')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/accounts') ? 'primary.main' : 'inherit' }}
                            >
                                <AccountBoxIcon />
                            </ListItemIcon>
                            <ListItemText primary={account.plural} sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('commodities') && (
                    <Tooltip title={!navOpen ? commodity.plural : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/commodities')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/commodities') ? 'primary.main' : 'inherit' }}
                            >
                                <StoreIcon />
                            </ListItemIcon>
                            <ListItemText primary={commodity.plural} sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('hauliers') && (
                    <Tooltip title={!navOpen ? 'Hauliers' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/hauliers')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/hauliers') ? 'primary.main' : 'inherit' }}
                            >
                                <LocalShippingIcon />
                            </ListItemIcon>
                            <ListItemText primary="Hauliers" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('drivers') && (
                    <Tooltip title={!navOpen ? 'Drivers' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/drivers')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/drivers') ? 'primary.main' : 'inherit' }}
                            >
                                <PersonPinCircleIcon />
                            </ListItemIcon>
                            <ListItemText primary="Drivers" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('locations') && (
                    <Tooltip title={!navOpen ? locationLabels.plural : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/locations')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/locations') ? 'primary.main' : 'inherit' }}
                            >
                                <CompassCalibrationIcon />
                            </ListItemIcon>
                            <ListItemText primary={locationLabels.plural} sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('laAreas') && (
                    <Tooltip title={!navOpen ? laAreaLabels.plural : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/la-areas')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/la-areas') ? 'primary.main' : 'inherit' }}
                            >
                                <MapIcon />
                            </ListItemIcon>
                            <ListItemText primary={laAreaLabels.plural} sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('depots') && (
                    <Tooltip title={!navOpen ? 'Depots' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/depots')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/depots') ? 'primary.main' : 'inherit' }}
                            >
                                <WarehouseIcon />
                            </ListItemIcon>
                            <ListItemText primary="Depots" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('ships') && (
                    <Tooltip title={!navOpen ? 'Ships' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/ships')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/ships') ? 'primary.main' : 'inherit' }}
                            >
                                <DirectionsBoatIcon />
                            </ListItemIcon>
                            <ListItemText primary="Ships" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('contracts') && (
                    <Tooltip title={!navOpen ? 'Contracts' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/contracts')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/contracts') ? 'primary.main' : 'inherit' }}
                            >
                                <HistoryEduIcon />
                            </ListItemIcon>
                            <ListItemText primary="Contracts" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('vehicles') && (
                    <Tooltip title={!navOpen ? 'Vehicles' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/vehicles')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/vehicles') ? 'primary.main' : 'inherit' }}
                            >
                                <AirportShuttleIcon />
                            </ListItemIcon>
                            <ListItemText primary="Vehicles" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('trailers') && (
                    <Tooltip title={!navOpen ? 'Trailers' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/trailers')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/trailers') ? 'primary.main' : 'inherit' }}
                            >
                                <RvHookupIcon />
                            </ListItemIcon>
                            <ListItemText primary="Trailers" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    {!isNavItemHidden('reports') && (
                    <Tooltip title={!navOpen ? 'Reports' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/reports')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/reports') ? 'primary.main' : 'inherit' }}
                            >
                                <AssessmentIcon />
                            </ListItemIcon>
                            <ListItemText primary="Reports" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* USERS (submenu parent – no Tooltip to avoid overlay conflicts) */}
                    {visibleUsersMenu.length > 0 && (
                    <>
                    <ListItemButton
                        sx={itemButtonSx}
                        onClick={(e) => (navOpen ? setUsersOpen((o) => !o) : openFlyout('users', e.currentTarget))}
                        onMouseEnter={(e) => { if (!navOpen) openFlyout('users', e.currentTarget); }}
                        onMouseLeave={() => { /* document mousemove handles close */ }}
                    >
                        <ListItemIcon
                            sx={{ ...itemIconSx, color: isNavActive('/users') ? 'primary.main' : 'inherit' }}
                        >
                            <PersonIcon />
                        </ListItemIcon>
                        <ListItemText primary="Users" sx={itemTextSx} />
                        {navOpen ? (usersOpen ? <ExpandLess /> : <ExpandMore />) : null}
                    </ListItemButton>
                    {/* Expanded in-drawer list (when open) */}
                    <Collapse in={navOpen && usersOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {visibleUsersMenu.map((it) => (
                                <ListItemButton key={it.label} sx={{ ...itemButtonSx, pl: 4 }} onClick={() => handleNav(it.path)}>
                                    <ListItemIcon sx={itemIconSx}>{it.icon}</ListItemIcon>
                                    <ListItemText primary={it.label} sx={itemTextSx} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                    {/* Flyout when collapsed */}
                    {!navOpen && <Flyout id="users" items={visibleUsersMenu} />}
                    </>
                    )}

                    {/* LOADS (submenu parent) */}
                    {visibleLoadsMenu.length > 0 && (
                    <>
                    <ListItemButton
                        sx={itemButtonSx}
                        onClick={(e) => (navOpen ? setLoadsOpen((o) => !o) : openFlyout('loads', e.currentTarget))}
                        onMouseEnter={(e) => { if (!navOpen) openFlyout('loads', e.currentTarget); }}
                        onMouseLeave={() => { /* document mousemove handles close */ }}
                    >
                        <ListItemIcon
                            sx={{ ...itemIconSx, color: location.pathname.startsWith('/loads') ? 'primary.main' : 'inherit' }}
                        >
                            <ScaleIcon />
                        </ListItemIcon>
                        <ListItemText primary="Loads" sx={itemTextSx} />
                        {navOpen ? (loadsOpen ? <ExpandLess /> : <ExpandMore />) : null}
                    </ListItemButton>
                    <Collapse in={navOpen && loadsOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {visibleLoadsMenu.map((it) => (
                                <ListItemButton key={it.label} sx={{ ...itemButtonSx, pl: 4 }} onClick={() => handleNav(it.path)}>
                                    <ListItemText primary={it.label} sx={itemTextSx} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                    {!navOpen && <Flyout id="loads" items={visibleLoadsMenu} />}
                    </>
                    )}

                    {/* PERMISSIONS (submenu parent) */}
                    {visiblePermsMenu.length > 0 && (
                    <>
                    <ListItemButton
                        sx={itemButtonSx}
                        onClick={(e) => (navOpen ? setPermissionsOpen((o) => !o) : openFlyout('perms', e.currentTarget))}
                        onMouseEnter={(e) => { if (!navOpen) openFlyout('perms', e.currentTarget); }}
                        onMouseLeave={() => { /* document mousemove handles close */ }}
                    >
                        <ListItemIcon
                            sx={{ ...itemIconSx, color: location.pathname.startsWith('/permissions') ? 'primary.main' : 'inherit' }}
                        >
                            <KeyIcon />
                        </ListItemIcon>
                        <ListItemText primary="Permissions" sx={itemTextSx} />
                        {navOpen ? (permissionsOpen ? <ExpandLess /> : <ExpandMore />) : null}
                    </ListItemButton>
                    <Collapse in={navOpen && permissionsOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {visiblePermsMenu.map((it) => (
                                <ListItemButton key={it.label} sx={{ ...itemButtonSx, pl: 4 }} onClick={() => handleNav(it.path)}>
                                    <ListItemIcon sx={itemIconSx}>{it.icon}</ListItemIcon>
                                    <ListItemText primary={it.label} sx={itemTextSx} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                    {!navOpen && <Flyout id="perms" items={visiblePermsMenu} />}
                    </>
                    )}

                    {/* Settings */}
                    {!isNavItemHidden('settings') && (
                    <Tooltip title={!navOpen ? 'Settings' : ''} placement="right">
                        <ListItemButton sx={itemButtonSx} onClick={() => handleNav('/settings')}>
                            <ListItemIcon
                                sx={{ ...itemIconSx, color: isNavActive('/settings') ? 'primary.main' : 'inherit' }}
                            >
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary="Settings" sx={itemTextSx} />
                        </ListItemButton>
                    </Tooltip>
                    )}

                </List>
            </Drawer>

            {/* Main content: flex child so form pages can fill and scroll individually */}
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', p: 3, display: 'flex', flexDirection: 'column' }}>
                <Toolbar />
                {liveWeightDisplayEnabled ? <LiveWeightStrip /> : null}
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Suspense fallback={
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    }>
                        <Outlet />
                    </Suspense>
                </Box>
                <AppFooter />
            </Box>

            {/* Logout dialog */}
            <Dialog open={confirmOpen} onClose={handleCancelLogout}>
                <DialogTitle>Confirm Logout</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to log out?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelLogout}>Cancel</Button>
                    <Button onClick={handleConfirmLogout} variant="contained" color="error">
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
